'use client';

import { useCallback, useState } from 'react';

/**
 * The return type of the @see {useModal} hook.
 *
 * It returns an object that serves as a controller for a modal.
 */
export interface IUseModalReturn {
  /**
   * Whether the modal is currently open.
   */
  isOpen: boolean;
  /**
   * An action to open the modal.
   */
  open: () => void;
  /**
   * An action to close the modal.
   */
  close: () => void;
  /**
   * An action to toggle the modal's open state.
   */
  toggle: () => void;
}

/**
 * Generic hook for handling the open / close state for modals.
 *
 * @param initialOpen - Initial state of the modal (default is false).
 * @returns An object containing the modal state and control functions.
 */
export const useModal = (initialOpen = false): IUseModalReturn => {
  const [isOpen, setIsOpen] = useState(initialOpen);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  return { isOpen, open, close, toggle };
};
